import type { AmmVenueRef, Instrument, PairListing } from '@nutrimatic/sdk'

import { toCatalogAddress } from '@/data/marketApi/normalizeMarketToken'
import type { PoolIdentity } from '@/data/marketApi/poolsTypes'

/** Extract pool address from `evm:…:amm:v2:0x…` venue id (canonical bridge key). */
export function poolAddressFromVenueId(venueId: string): string | undefined {
  const match = /:(0x[a-fA-F0-9]{40})$/.exec(venueId)
  return match?.[1]?.toLowerCase()
}

export function normalizeAddressKey(address: string | undefined | null): string {
  if (!address) return ''
  return toCatalogAddress(address).toLowerCase()
}

export function instrumentAddressKey(instrument: Instrument): string {
  if (instrument.onChainAddress) {
    return normalizeAddressKey(instrument.onChainAddress)
  }
  return ''
}

export type PairBridgeEntry = {
  pairLabel: string
  venueId: string
  pairAddress: string
  baseInstrumentId: string
  quoteInstrumentId: string
  poolId?: string
}

/** Index Nutrimatic listings by pair address for merge with product-config seed. */
export function buildPairBridgeByAddress(
  listings: readonly PairListing[],
  venues?: readonly AmmVenueRef[],
): Map<string, PairBridgeEntry> {
  const venuePoolById = new Map<string, string>()
  for (const venue of venues ?? []) {
    if (venue.poolAddress) {
      venuePoolById.set(venue.id, venue.poolAddress.toLowerCase())
    }
  }

  const map = new Map<string, PairBridgeEntry>()
  for (const listing of listings) {
    const fromVenue = venuePoolById.get(listing.venueId)
    const fromId = poolAddressFromVenueId(listing.venueId)
    const pairAddress = (fromVenue ?? fromId ?? '').toLowerCase()
    if (!pairAddress) continue
    map.set(pairAddress, {
      pairLabel: listing.pairLabel,
      venueId: listing.venueId,
      pairAddress,
      baseInstrumentId: listing.baseInstrumentId,
      quoteInstrumentId: listing.quoteInstrumentId,
    })
  }
  return map
}

export function findBridgeForPool(
  pool: Pick<PoolIdentity, 'pairAddress' | 'id'>,
  bridgeByAddress: ReadonlyMap<string, PairBridgeEntry>,
): PairBridgeEntry | undefined {
  const byAddr = bridgeByAddress.get(normalizeAddressKey(pool.pairAddress))
  if (byAddr) return { ...byAddr, poolId: pool.id }
  return undefined
}

export function findBridgeByPairAddress(
  pairAddress: string | undefined,
  bridgeByAddress: ReadonlyMap<string, PairBridgeEntry>,
): PairBridgeEntry | undefined {
  if (!pairAddress) return undefined
  return bridgeByAddress.get(normalizeAddressKey(pairAddress))
}

/** Resolve Don't Panic token address → Nutrimatic instrument id. */
export function findInstrumentIdByAddress(
  address: string | undefined,
  instruments: readonly Instrument[],
): string | undefined {
  const key = normalizeAddressKey(address)
  if (!key) return undefined
  const hit = instruments.find((i) => instrumentAddressKey(i) === key)
  return hit?.id
}

export function findInstrumentByAddress(
  address: string | undefined,
  instruments: readonly Instrument[],
): Instrument | undefined {
  const key = normalizeAddressKey(address)
  if (!key) return undefined
  return instruments.find((i) => instrumentAddressKey(i) === key)
}
